import { useEffect, useRef, useState } from 'react';
import {
  Clock,
  Search,
  Server,
  Users,
  X,
  ChevronDown,
  Terminal,
  Loader,
  CheckCircle2,
} from 'lucide-react';
import { format, subHours } from 'date-fns';
import { PlayerSearchField, type PlayerSelection } from './PlayerSearchField';
import { eventTeamApiService } from '../../services/eventTeamApi';
import { serverListApiService } from '../../services/serverListApi';
import {
  canonicalPlayerUuid,
  searchEventCommands,
  type CommandSearchResult,
} from '../../services/eventCommandSearch';
import type { EventPlayerTeam } from '../../types/eventTeam';
import { useDebounce } from '../../hooks/useDebounce';
import { StatTile } from '../ui/StatTile';
import { EmptyState } from '../ui/EmptyState';
import { cn } from '../../utils/cn';

const localInput = (date: Date) => format(date, "yyyy-MM-dd'T'HH:mm");
const textValue = (value: unknown) =>
  typeof value === 'string' || typeof value === 'number' ? String(value) : '';
const logTime = (value: unknown) => {
  if (typeof value !== 'string' && typeof value !== 'number')
    return 'Time unavailable';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? format(date, 'd MMM, HH:mm:ss')
    : 'Time unavailable';
};

export function QuickCommandSearch() {
  const [players, setPlayers] = useState<PlayerSelection[]>([]);
  const [groups, setGroups] = useState<EventPlayerTeam[]>([]);
  const [groupSearch, setGroupSearch] = useState('');
  const debouncedGroup = useDebounce(groupSearch, 300);
  const [groupError, setGroupError] = useState('');
  const [groupLoading, setGroupLoading] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [server, setServer] = useState('');
  const [servers, setServers] = useState<{ address: string; name: string }[]>(
    [],
  );
  const [start, setStart] = useState(() =>
    localInput(subHours(new Date(), 24)),
  );
  const [end, setEnd] = useState(() => localInput(new Date()));
  const [commands, setCommands] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<CommandSearchResult | null>(null);
  const [error, setError] = useState('');
  const [playerFilter, setPlayerFilter] = useState('');
  const [logPage, setLogPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const [resultRevision, setResultRevision] = useState(0);
  const mounted = useRef(true);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    mounted.current = true;
    serverListApiService
      .getServerList()
      .then((list) => {
        if (!mounted.current) return;
        const options = list.flatMap((s) => [
          { address: s.serverAddress, name: s.modpackTitle },
          ...(s.clusterChildren ?? []).map((c) => ({
            address: c.serverAddress,
            name: `${s.modpackTitle} · ${c.regionName}`,
          })),
        ]);
        setServers(
          Array.from(new Map(options.map((s) => [s.address, s])).values()),
        );
      })
      .catch(() => {
        /* A server address can still be entered manually. */
      });
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!showGroups) return;
    let cancelled = false;
    setGroupLoading(true);
    setGroupError('');
    eventTeamApiService
      .listCheckTeams({
        search: debouncedGroup || undefined,
        page: 1,
        limit: 20,
      })
      .then((res) => {
        if (!cancelled) setGroups(res.results);
      })
      .catch(() => {
        if (!cancelled)
          setGroupError(
            'Could not load saved groups. Try another search or add players individually.',
          );
      })
      .finally(() => {
        if (!cancelled) setGroupLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showGroups, debouncedGroup]);

  const changed = () => {
    setRevision((n) => n + 1);
    setError('');
  };
  const addPlayers = (incoming: PlayerSelection[]) => {
    setPlayers((current) =>
      Array.from(
        new Map(
          [...current, ...incoming].map((p) => [
            canonicalPlayerUuid(p.uuid),
            p,
          ]),
        ).values(),
      ),
    );
    changed();
  };
  const addGroup = async (group: EventPlayerTeam) => {
    setAddingGroup(true);
    setGroupError('');
    try {
      const members = await eventTeamApiService.listCheckTeamMembers(group.id);
      if (!mounted.current) return;
      if (!members.length) {
        setGroupError(`${group.name} has no players yet.`);
        return;
      }
      addPlayers(
        members.map((m) => ({ uuid: m.user_uuid, name: m.display_name })),
      );
      setShowGroups(false);
    } catch {
      if (mounted.current)
        setGroupError('Could not load this group. Try again.');
    } finally {
      if (mounted.current) setAddingGroup(false);
    }
  };

  const run = async (event: React.FormEvent) => {
    event.preventDefault();
    if (running) return;
    setRunning(true);
    setError('');
    setResult(null);
    setPlayerFilter('');
    setLogPage(1);
    try {
      const next = await searchEventCommands({
        players,
        server: server.trim(),
        start: new Date(start).getTime(),
        end: new Date(end).getTime(),
        commands,
      });
      if (!mounted.current) return;
      setResult(next);
      setResultRevision(revision);
      requestAnimationFrame(() => resultRef.current?.focus());
    } catch (err) {
      if (mounted.current)
        setError(
          err instanceof Error
            ? err.message
            : 'Could not search command activity.',
        );
    } finally {
      if (mounted.current) setRunning(false);
    }
  };
  const logs =
    result?.hits.filter(
      (hit) =>
        !playerFilter ||
        canonicalPlayerUuid(
          textValue(hit._source[result.mapping.player_uuid]),
        ) === playerFilter,
    ) ?? [];

  return (
    <div className="space-y-5">
      <form onSubmit={run} className="glass-card p-4 sm:p-6 space-y-5">
        <div className="flex items-start gap-3">
          <span className="icon-tile icon-tile-accent">
            <Terminal className="w-5 h-5" />
          </span>
          <div>
            <h2 className="text-lg font-semibold text-white">
              Check command activity
            </h2>
            <p className="text-sm text-glass-400 mt-1">
              Pick players, an event server and a time range. Get an answer in
              one search.
            </p>
          </div>
        </div>
        <fieldset disabled={running} className="space-y-5 min-w-0">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="space-y-3 min-w-0">
              <h3 className="text-sm font-medium text-white inline-flex items-center gap-2">
                <Users className="w-4 h-4 text-accent-400" />
                Who are you checking?
              </h3>
              <div data-tour="event-command-players"><PlayerSearchField
                value={null}
                onChange={(p) => {
                  if (p) addPlayers([p]);
                }}
                placeholder="Find a player by name or UUID…"
              /></div>
              {players.length > 0 && (
                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
                  {players.map((p) => (
                    <span
                      className="chip max-w-full"
                      key={canonicalPlayerUuid(p.uuid)}
                    >
                      <span className="truncate">{p.name ?? p.uuid}</span>
                      <button
                        type="button"
                        className="btn-icon min-h-[44px] min-w-[44px]"
                        aria-label={`Remove ${p.name ?? p.uuid}`}
                        onClick={() => {
                          setPlayers((current) =>
                            current.filter(
                              (x) =>
                                canonicalPlayerUuid(x.uuid) !==
                                canonicalPlayerUuid(p.uuid),
                            ),
                          );
                          changed();
                        }}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <button
                type="button"
                className="glass-button-secondary text-xs"
                aria-expanded={showGroups}
                onClick={() => setShowGroups(!showGroups)}
              >
                <Users className="w-4 h-4" />
                Add a saved group
                <ChevronDown
                  className={cn('w-3.5 h-3.5', showGroups && 'rotate-180')}
                />
              </button>
              {showGroups && (
                <div className="glass-card p-3 space-y-2">
                  <input
                    aria-label="Search saved groups"
                    className="glass-input w-full"
                    value={groupSearch}
                    onChange={(e) => setGroupSearch(e.target.value)}
                    placeholder="Find a group…"
                  />
                  {groupError && (
                    <p className="text-sm text-red-400" role="alert">
                      {groupError}
                    </p>
                  )}
                  {groupLoading ? (
                    <p className="text-sm text-glass-400" role="status">
                      Loading groups…
                    </p>
                  ) : (
                    <div className="max-h-48 overflow-auto">
                      {groups.map((g) => (
                        <button
                          type="button"
                          disabled={addingGroup}
                          key={g.id}
                          className="w-full text-left min-h-[44px] px-2 rounded-lg hover:bg-glass-200/10 text-sm text-white"
                          onClick={() => void addGroup(g)}
                        >
                          {g.name}
                        </button>
                      ))}
                      {!groups.length && !groupError && (
                        <p className="text-sm text-glass-400">
                          No groups found. Add players by name above.
                        </p>
                      )}
                    </div>
                  )}
                  {addingGroup && (
                    <p className="text-xs text-glass-400" role="status">
                      Adding players…
                    </p>
                  )}
                </div>
              )}
              <p className="text-xs text-glass-500">
                {players.length
                  ? `${players.length} player${players.length === 1 ? '' : 's'} selected · `
                  : ''}
                Add individuals or combine groups. Duplicate players count once.
              </p>
            </div>
            <div className="space-y-4 min-w-0">
              <label data-tour="event-command-server" className="block space-y-2">
                <span className="text-sm font-medium text-white inline-flex items-center gap-2">
                  <Server className="w-4 h-4 text-accent-400" />
                  Which event server?
                </span>
                <input
                  required
                  list="command-search-servers"
                  className="glass-input w-full"
                  placeholder="Choose or enter a server address…"
                  value={server}
                  onChange={(e) => {
                    setServer(e.target.value);
                    changed();
                  }}
                />
              </label>
              <datalist id="command-search-servers">
                {servers.map((s) => (
                  <option key={s.address} value={s.address}>
                    {s.name}
                  </option>
                ))}
              </datalist>
              <div data-tour="event-command-time" className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-sm font-medium text-white inline-flex items-center gap-2">
                    <Clock className="w-4 h-4 text-accent-400" />
                    When?
                  </h3>
                  <div className="flex gap-1">
                    {[1, 24, 168].map((hours) => (
                      <button
                        type="button"
                        key={hours}
                        className="glass-button-secondary text-xs"
                        onClick={() => {
                          const now = new Date();
                          setStart(localInput(subHours(now, hours)));
                          setEnd(localInput(now));
                          changed();
                        }}
                      >
                        {hours === 1
                          ? 'Last hour'
                          : hours === 24
                            ? '24 hours'
                            : '7 days'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <label className="text-xs text-glass-400 space-y-1 block">
                    From
                    <input
                      type="datetime-local"
                      required
                      className="glass-input w-full min-w-0"
                      value={start}
                      onChange={(e) => {
                        setStart(e.target.value);
                        changed();
                      }}
                    />
                  </label>
                  <label className="text-xs text-glass-400 space-y-1 block">
                    To
                    <input
                      type="datetime-local"
                      required
                      className="glass-input w-full min-w-0"
                      value={end}
                      onChange={(e) => {
                        setEnd(e.target.value);
                        changed();
                      }}
                    />
                  </label>
                </div>
                <p className="text-xs text-glass-500">
                  Times shown in{' '}
                  {Intl.DateTimeFormat().resolvedOptions().timeZone}.
                </p>
              </div>
            </div>
          </div>
          <details className="border-t border-glass-200/10 pt-4">
            <summary className="text-sm text-glass-300 cursor-pointer min-h-[44px]">
              {commands.trim()
                ? `Specific commands: ${commands}`
                : 'Narrow to specific commands (optional)'}
            </summary>
            <label className="block text-xs text-glass-400 space-y-2 max-w-xl">
              Command names
              <input
                className="glass-input w-full"
                placeholder="/fly, /give, /gamemode"
                value={commands}
                onChange={(e) => {
                  setCommands(e.target.value);
                  changed();
                }}
              />
              <span className="block">
                Leave blank to find any command. Separate command names with
                commas; arguments are included automatically.
              </span>
            </label>
          </details>
        </fieldset>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-glass-200/10 pt-4">
          <p className="text-xs text-glass-400">
            Search results appear here. Saved checks are available separately.
          </p>
          <button
            type="submit"
            data-tour="event-command-run"
            disabled={
              running ||
              addingGroup ||
              !players.length ||
              !server.trim() ||
              !start ||
              !end
            }
            className="btn-primary min-h-[44px]"
          >
            {running ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Search className="w-4 h-4" />
            )}
            {running ? 'Searching activity…' : 'Search command activity'}
          </button>
        </div>
      </form>
      {!result && !running && !error && (
        <EmptyState
          icon={Search}
          title="An answer for every player"
          description="See who used commands, how often, and the matching log entries, all in one place."
        />
      )}
      {running && (
        <div className="glass-card p-6 text-sm text-glass-300" role="status">
          Searching command logs and counting activity for each player…
        </div>
      )}
      {result && (
        <div
          ref={resultRef}
          tabIndex={-1}
          className="space-y-4 outline-none"
          aria-label="Command search results"
        >
          {revision !== resultRevision && (
            <div className="alert alert-warning" role="status">
              Your search has changed. Search again to update these results.
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-start justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">
                {result.total
                  ? 'Command activity found'
                  : 'No matching command activity'}
              </h3>
              <p className="text-xs text-glass-400 mt-1 break-all">
                {result.input.server} ·{' '}
                {format(new Date(result.input.start), 'd MMM yyyy, HH:mm')} –{' '}
                {format(new Date(result.input.end), 'd MMM yyyy, HH:mm')}
              </p>
              <p className="text-xs text-glass-500 mt-1">
                {result.input.commands.trim()
                  ? `Commands: ${result.input.commands}`
                  : 'All commands'}{' '}
                · {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>
            <span className="badge badge-neutral">
              {result.players.length} players checked
            </span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <StatTile
              label="Matching commands"
              value={result.total.toLocaleString()}
              icon={Terminal}
              tone="accent"
            />
            <StatTile
              label="Players with activity"
              value={result.players.filter((p) => p.count > 0).length}
              icon={Users}
            />
            <StatTile
              label="No matches"
              value={result.players.filter((p) => !p.count).length}
              icon={CheckCircle2}
            />
          </div>
          <div className="glass-card p-4 space-y-3">
            <div className="flex flex-wrap gap-3 justify-between">
              <h4 className="text-sm font-medium text-white">By player</h4>
              {playerFilter && (
                <button
                  type="button"
                  className="text-xs text-accent-300"
                  onClick={() => {
                    setPlayerFilter('');
                    setLogPage(1);
                  }}
                >
                  Show all players
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
              {result.players.map((p) => (
                <button
                  type="button"
                  key={p.uuid}
                  aria-pressed={playerFilter === canonicalPlayerUuid(p.uuid)}
                  className={cn(
                    'glass-button-secondary justify-between min-w-0 min-h-[44px]',
                    playerFilter === canonicalPlayerUuid(p.uuid) &&
                      'border-accent-400/50',
                  )}
                  onClick={() => {
                    setPlayerFilter(
                      playerFilter === canonicalPlayerUuid(p.uuid)
                        ? ''
                        : canonicalPlayerUuid(p.uuid),
                    );
                    setLogPage(1);
                  }}
                >
                  <span className="truncate">{p.name ?? p.uuid}</span>
                  <span className="tabular-nums shrink-0">
                    {p.count.toLocaleString()}
                  </span>
                </button>
              ))}
            </div>
            <p className="text-xs text-glass-500">
              Counts cover the full search window. Choose a player to filter the
              recent entries below.
            </p>
          </div>
          {result.total > 0 && (
            <div className="glass-card p-4 space-y-3">
              <h4 className="text-sm font-medium text-white">
                Recent matching entries
              </h4>
              {result.total > result.hits.length && (
                <p className="text-xs text-glass-400">
                  Showing the latest {result.hits.length} of{' '}
                  {result.total.toLocaleString()} matches. Narrow the time range
                  for older entries.
                </p>
              )}
              <div className="divide-y divide-glass-200/10">
                {logs.slice((logPage - 1) * 20, logPage * 20).map((hit) => (
                  <div
                    key={`${hit._index}/${hit._id}`}
                    className="py-3 space-y-1"
                  >
                    <div className="flex flex-wrap justify-between gap-2 text-xs text-glass-400">
                      <span>
                        {result.players.find(
                          (p) =>
                            canonicalPlayerUuid(p.uuid) ===
                            canonicalPlayerUuid(
                              textValue(
                                hit._source[result.mapping.player_uuid],
                              ),
                            ),
                        )?.name ??
                          textValue(hit._source.fromPlayerName) ??
                          textValue(hit._source[result.mapping.player_uuid])}
                      </span>
                      <time>
                        {logTime(hit._source[result.mapping.timestamp])}
                      </time>
                    </div>
                    <code className="block text-sm text-white whitespace-pre-wrap break-all">
                      {textValue(hit._source[result.mapping.command])}
                      {hit._source.args
                        ? ` ${textValue(hit._source.args)}`
                        : ''}
                    </code>
                  </div>
                ))}
              </div>
              {!logs.length && (
                <p className="text-sm text-glass-400">
                  No entries for this player in the latest {result.hits.length}{' '}
                  matches. Narrow your search to see their activity.
                </p>
              )}
              {logs.length > 20 && (
                <div className="flex justify-end items-center gap-3 text-xs text-glass-400">
                  <button
                    type="button"
                    className="glass-button-secondary"
                    disabled={logPage === 1}
                    onClick={() => setLogPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {logPage} / {Math.ceil(logs.length / 20)}
                  </span>
                  <button
                    type="button"
                    className="glass-button-secondary"
                    disabled={logPage * 20 >= logs.length}
                    onClick={() => setLogPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          {!result.total && (
            <p className="text-xs text-glass-500">
              No matching entries were found in the available logs for this
              server and time range.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
